import { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Collapse } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandLess } from "@mui/icons-material";

import MegaMenuItemPresenter from "presenters/MegaMenuItemPresenter";

import { LinkList, ExternalStyledMenuItem, InternalStyledMenuItem, LinkInList, CollapsableMenuItem } from "./styles";

const MenuItem = ({ menuItem, closeMenu, onClick, mainMenuLinks }) => {
  const [collapseSubMenu, setCollapseSubMenu] = useState(false);
  const location = useLocation();

  const name = MegaMenuItemPresenter.name(menuItem);
  const subMenuItmes = MegaMenuItemPresenter.childrenMenu(menuItem);

  const hasActiveSubLink = useMemo(() => {
    if (!subMenuItmes || subMenuItmes.length === 0) return false;

    const currentPath = location.pathname;
    const matchingSubLink = subMenuItmes.find((subMenu) => MegaMenuItemPresenter.linkTo(subMenu) === currentPath);

    if (!matchingSubLink) return false;

    return !mainMenuLinks.includes(MegaMenuItemPresenter.linkTo(matchingSubLink));
  }, [subMenuItmes, location.pathname, mainMenuLinks]);

  const handleClick = useCallback(
    (e, path) => {
      if (onClick) onClick(e, path);
      if (closeMenu) closeMenu();
    },
    [onClick, closeMenu],
  );

  const handleToggleSubMenu = useCallback(() => {
    setCollapseSubMenu(!collapseSubMenu);
  }, [collapseSubMenu]);

  const renderSimpleLink = () => {
    return MegaMenuItemPresenter.isExternalLinkTo(menuItem) ? (
      <ExternalStyledMenuItem
        id={MegaMenuItemPresenter.createComponentId(menuItem)}
        href={MegaMenuItemPresenter.linkTo(menuItem)}
        onClick={handleClick}
      >
        {name}
      </ExternalStyledMenuItem>
    ) : (
      <InternalStyledMenuItem
        id={MegaMenuItemPresenter.createComponentId(menuItem)}
        exact
        to={MegaMenuItemPresenter.linkTo(menuItem)}
        component={NavLink}
        onClick={(e) => handleClick(e, MegaMenuItemPresenter.linkTo(menuItem))}
      >
        {name}
      </InternalStyledMenuItem>
    );
  };

  const renderCollapsableLink = () => {
    return (
      <>
        <CollapsableMenuItem onClick={handleToggleSubMenu} hasActiveSubLink={hasActiveSubLink}>
          <Button
            id={`button-${MegaMenuItemPresenter.createComponentId(menuItem)}`}
            variant="link"
            endIcon={collapseSubMenu ? <ExpandLess /> : <ExpandMoreIcon />}
          >
            {name}
          </Button>
        </CollapsableMenuItem>

        <Collapse hidden={!collapseSubMenu} in={collapseSubMenu} onClick={handleToggleSubMenu}>
          <LinkList>
            {subMenuItmes.map((subMenu) => (
              <LinkInList
                id={MegaMenuItemPresenter.createComponentId(subMenu)}
                key={MegaMenuItemPresenter.name(subMenu)}
                to={MegaMenuItemPresenter.linkTo(subMenu)}
                component={NavLink}
                onClick={(e) => handleClick(e, MegaMenuItemPresenter.linkTo(subMenu))}
              >
                {subMenu.linkText}
              </LinkInList>
            ))}
          </LinkList>
        </Collapse>
      </>
    );
  };

  return subMenuItmes && subMenuItmes.length === 0 ? renderSimpleLink() : renderCollapsableLink();
};

MenuItem.propTypes = {
  menuItem: MegaMenuItemPresenter.shape(),
  closeMenu: PropTypes.func,
  onClick: PropTypes.func,
  mainMenuLinks: PropTypes.arrayOf(PropTypes.string),
};

export default MenuItem;
